import { useI18n } from 'vue-i18n'
import { inject, InjectionKey } from 'vue'

// Define an interface for the date formatting functions
export interface DateFormatters {
  formatDate: (date: Date, options?: Intl.DateTimeFormatOptions) => string
  formatShortDate: (date: Date) => string
  formatBookingDate: (date: Date) => string
  formatDateWithoutYear: (date: Date) => string
  formatDay: (date: Date) => string
  isSameDate: (date1: Date | null, date2: Date | null) => boolean
  createBookingMessage: (date: Date | null, time: string | null) => string
  getShortWeekday: (date: Date) => string
  formatTime: (timeString: string) => string
  isPastDate: (date: Date) => boolean
}

// Define a symbol for i18n injection
export const I18nFunctionsKey: InjectionKey<DateFormatters> = Symbol('i18nFunctions')

/**
 * Composable that provides i18n-dependent date formatting functions
 * This MUST be called from within a component's setup function
 */
export function useDateFormatting(): DateFormatters {
  const { locale, d, t } = useI18n()
  
  const formatDate = (date: Date, options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }): string => {
    if (!date) return ''
    return d(date, 'long')
  }
  
  const formatShortDate = (date: Date): string => {
    if (!date) return ''
    return d(date, 'short')
  }
  
  const formatBookingDate = (date: Date): string => {
    if (!date) return ''
    return d(date, 'booking')
  }
  
  /**
   * Format a date without the year component
   */
  const formatDateWithoutYear = (date: Date): string => {
    if (!date) return ''
    return d(date, 'dateWithoutYear')
  }
  
  /**
   * Format a date with special handling for today, tomorrow, and this week
   * Returns "Today", "Tomorrow", day name, or full date based on the date
   */
  const formatDay = (date: Date): string => {
    if (!date) return ''
    
    // Get today and tomorrow dates for comparison
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    
    // Set input date to midnight for proper comparison
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    
    // Check if date is today
    if (compareDate.getTime() === today.getTime()) {
      return t('dates.today', 'Today')
    }
    
    // Check if date is tomorrow
    if (compareDate.getTime() === tomorrow.getTime()) {
      return t('dates.tomorrow', 'Tomorrow')
    }
    
    // If date is within the next 6 days, return the weekday
    const nextWeek = new Date(today)
    nextWeek.setDate(today.getDate() + 6)
    
    if (compareDate > today && compareDate <= nextWeek) {
      return new Intl.DateTimeFormat(locale.value, { weekday: 'long' }).format(date)
    }
    
    // Otherwise return the full date without year
    return formatDateWithoutYear(date)
  }
  
  /**
   * Check if two dates represent the same day, ignoring time
   */
  const isSameDate = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false
    
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    )
  }
  
  /**
   * Check if a date is in the past
   */
  const isPastDate = (date: Date): boolean => {
    if (!date) return false
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    
    return compareDate < today
  }
  
  /**
   * Create a booking confirmation message with formatted date and time
   */
  const createBookingMessage = (date: Date | null, time: string | null): string => {
    if (!date || !time) {
      return t('booking.genericConfirmation', 'Your booking is confirmed')
    }
    
    // Format the time
    const formattedTime = formatTime(time)
    
    // Get today and tomorrow dates for comparison
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    
    // Compare the date
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    
    // Use different message types based on the date
    if (compareDate.getTime() === today.getTime()) {
      // For today
      return t('booking.confirmationToday', { time: formattedTime }, `See you today at ${formattedTime}`)
    } else if (compareDate.getTime() === tomorrow.getTime()) {
      // For tomorrow
      return t('booking.confirmationTomorrow', { time: formattedTime }, `See you tomorrow at ${formattedTime}`)
    } else {
      // For any other day
      const formattedDay = formatDateWithoutYear(date)
      return t('booking.confirmation', { day: formattedDay, time: formattedTime }, 
        `See you on ${formattedDay} at ${formattedTime}`)
    }
  }
  
  const getShortWeekday = (date: Date): string => {
    if (!date) return ''
    return d(date, 'weekday')
  }
  
  const formatTime = (timeString: string): string => {
    if (!timeString) return ''
    
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours, 10)
    
    if (isNaN(hour) || isNaN(parseInt(minutes, 10))) {
      return timeString
    }
    
    // Create a Date object with the hours and minutes
    const date = new Date()
    date.setHours(hour)
    date.setMinutes(parseInt(minutes, 10))
    
    return d(date, 'time')
  }
  
  return {
    formatDate,
    formatShortDate,
    formatBookingDate,
    formatDateWithoutYear,
    formatDay,
    isSameDate,
    createBookingMessage,
    getShortWeekday,
    formatTime,
    isPastDate
  }
}

// Static non-composable utility functions for use outside of Vue components
export const dateUtils = {
  /**
   * Format a date without requiring i18n (standalone version)
   */
  formatDateStandalone: (date: Date): string => {
    if (!date) return ''
    
    const locale = localStorage.getItem('user-locale') || 'en'
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date)
  },
  
  /**
   * Format a short date without requiring i18n (standalone version)
   */
  formatShortDateStandalone: (date: Date): string => {
    if (!date) return ''
    
    const locale = localStorage.getItem('user-locale') || 'en'
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date)
  },
  
  /**
   * Format a booking date without requiring i18n (standalone version)
   */
  formatBookingDateStandalone: (date: Date): string => {
    if (!date) return ''
    
    const locale = localStorage.getItem('user-locale') || 'en'
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }).format(date)
  },
  
  /**
   * Format a date without the year component (standalone version)
   */
  formatDateWithoutYearStandalone: (date: Date): string => {
    if (!date) return ''
    
    const locale = localStorage.getItem('user-locale') || 'en'
    return new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    }).format(date)
  },
  
  /**
   * Format a time string without requiring i18n (standalone version)
   */
  formatTimeStandalone: (timeString: string): string => {
    if (!timeString) return ''
    
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours, 10)
    const minute = parseInt(minutes, 10)
    
    if (isNaN(hour) || isNaN(minute)) {
      return timeString
    }
    
    const date = new Date()
    date.setHours(hour, minute, 0, 0)
    
    const locale = localStorage.getItem('user-locale') || 'en'
    return new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).format(date)
  },
  
  /**
   * Get the short weekday name without requiring i18n (standalone version)
   */
  getShortWeekdayStandalone: (date: Date): string => {
    if (!date) return ''
    
    const locale = localStorage.getItem('user-locale') || 'en'
    return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date)
  },
  
  /**
   * Check if a date is in the past
   */
  isPastDate: (date: Date): boolean => {
    if (!date) return false
    
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)
    
    return compareDate < today
  },
  
  /**
   * Check if two dates represent the same day, ignoring time
   */
  isSameDate: (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false
    
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    )
  },
  
  /**
   * Get the day number as a string
   */
  getDayNumber: (date: Date): string => {
    if (!date) return ''
    return date.getDate().toString()
  }
}
